<template>
    <div
        id="connect-step-1"
        class="connect-step flex flex-col flex-1 min-h-0 h-full"
        v-show="connectStep === 1"
    >
        <div
            class="flex w-full flex-col min-h-0 flex-1 h-full"
            :class="[
                isStep1Mobile
                    ? 'pb-24 h-full'
                    : 'rounded-lg bg-white dark:bg-slate-800',
            ]"
        >
            <!-- Content Area -->
            <div
                class="flex flex-col p-6 gap-8 flex-1 min-h-0 overflow-y-auto"
            >
                <div
                    class="flex items-start justify-between self-stretch"
                >
                    <div
                        class="flex flex-col justify-center items-start gap-1 flex-1"
                    >
                        <h2
                            class="flex-1 text-slate-800 dark:text-slate-100 text-xl font-medium leading-7"
                        >
                            Add card
                        </h2>
                        <p
                            class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5"
                        >
                            Select add method
                        </p>
                    </div>
                    <a
                        href="#"
                        class="btn-icon btn-transparent w-10"
                        role="button"
                        @click.prevent="$emit('close')"
                        id="hs-wallet-close-connect-modal"
                    >
                        <Icon icon="close" />
                    </a>
                </div>
                <!-- User dropdown - Only show when there are multiple users -->
                <div
                    v-if="users.length > 1"
                    class="flex flex-col items-start self-stretch w-full"
                >
                    <UiInput
                        id="user_id"
                        name="user_id"
                        :options="{
                            wrapperClass: 'w-full',
                            labelInputWrapperClass: 'w-full',
                        }"
                    >
                        <template #label>
                            <label
                                for="user_id"
                                class="block text-sm font-medium leading-tight mb-2"
                            >
                                <span
                                    class="text-sm font-medium leading-5 text-slate-600 dark:text-slate-300"
                                >
                                    User
                                </span>
                            </label>
                        </template>
                        <template #input>
                            <div
                                class="relative w-full"
                                data-dropdown="user"
                            >
                                <button
                                    type="button"
                                    @click="$emit('toggleUserDropdown')"
                                    ref="userDropdownBtn"
                                    class="form-input w-full text-left flex items-center justify-between px-4 py-2.0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                                >
                                    <div
                                        class="flex items-center gap-2 grow shrink-0 basis-0"
                                    >
                                        <Icon
                                            icon="user"
                                            class="w-5 h-5 text-slate-800 dark:text-slate-100"
                                        />
                                        <span
                                            class="text-sm font-normal leading-5"
                                            :class="
                                                selectedUserName
                                                    ? 'text-slate-800 dark:text-slate-100'
                                                    : 'text-slate-500 dark:text-slate-400'
                                            "
                                        >
                                            {{
                                                selectedUserName ||
                                                'Select user'
                                            }}
                                        </span>
                                    </div>
                                    <Icon
                                        icon="chevron-down"
                                        class="w-3 h-3 text-slate-400"
                                        :class="{
                                            'rotate-180':
                                                showUserDropdown,
                                        }"
                                    />
                                </button>
                                <Teleport to="body">
                                    <div
                                        v-if="showUserDropdown"
                                        ref="userDropdownMenu"
                                        data-dropdown-options="user"
                                        class="fixed z-[99999] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-60 overflow-y-auto"
                                        :style="
                                            userDropdownStyles
                                        "
                                    >
                                        <div
                                            v-for="user in users"
                                            :key="user.id"
                                            @click="
                                                $emit('selectUser', user)
                                            "
                                            class="px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-2"
                                        >
                                            <Icon
                                                icon="user"
                                                class="w-4 h-4 text-slate-600 dark:text-slate-300"
                                            />
                                            {{ user.name }}
                                        </div>
                                    </div>
                                </Teleport>
                            </div>
                        </template>
                    </UiInput>
                </div>

                <!-- Method Selection -->
                <div
                    class="flex flex-col items-start gap-2 self-stretch"
                    v-if="
                        users.length <= 1 ||
                        accountUserForCard
                    "
                >
                    <!-- Method Label -->
                    <label
                        class="text-slate-600 dark:text-slate-300 text-sm font-medium leading-5"
                    >
                        Method
                    </label>

                    <!-- Options Container -->
                    <div
                        class="flex flex-col justify-center items-start gap-2 self-stretch"
                    >
                        <!-- Connect via Bank Option -->
                        <label
                            class="flex p-3 items-center self-stretch rounded-lg cursor-pointer transition-all duration-200 relative"
                            :class="
                                howToAddCard ===
                                'connect_to_bank'
                                    ? 'border-2 border-[#FD421D]'
                                    : 'border border-slate-300 dark:border-slate-600'
                            "
                        >
                            <input
                                type="radio"
                                class="sr-only"
                                value="connect_to_bank"
                                :checked="howToAddCard === 'connect_to_bank'"
                                @change="$emit('update:howToAddCard', 'connect_to_bank')"
                            />

                            <!-- Radio Circle -->
                            <div class="absolute top-3 right-3">
                                <div
                                    :class="[
                                        'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                                        howToAddCard ===
                                        'connect_to_bank'
                                            ? 'border-[#FD421D] bg-[#FD421D]'
                                            : 'border-slate-300 bg-white dark:bg-slate-800',
                                    ]"
                                >
                                    <div
                                        v-if="
                                            howToAddCard ===
                                            'connect_to_bank'
                                        "
                                        class="w-2 h-2 bg-white dark:bg-slate-800 rounded-full"
                                    ></div>
                                </div>
                            </div>

                            <!-- Content Container -->
                            <div
                                class="flex flex-col items-start gap-2 flex-1"
                            >
                                <!-- Icon Container -->
                                <div
                                    class="flex w-5 h-5 justify-start items-center"
                                >
                                    <Icon
                                        icon="bank-financial"
                                        class="w-5 h-5 text-slate-600 dark:text-slate-300"
                                    />
                                </div>

                                <!-- Title -->
                                <h3
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    Connect account via bank
                                </h3>

                                <!-- Features List -->
                                <div
                                    class="flex flex-col items-start gap-1 self-stretch"
                                >
                                    <div
                                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 flex items-center"
                                    >
                                        <span
                                            class="w-1 h-1 bg-slate-400 rounded-full mr-2 flex-shrink-0"
                                        ></span>
                                        Instant automatic
                                        updates
                                    </div>
                                    <div
                                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 flex items-center"
                                    >
                                        <span
                                            class="w-1 h-1 bg-slate-400 rounded-full mr-2 flex-shrink-0"
                                        ></span>
                                        Adds all bank cards and
                                        bonus programs
                                    </div>
                                    <div
                                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 flex items-center"
                                    >
                                        <span
                                            class="w-1 h-1 bg-slate-400 rounded-full mr-2 flex-shrink-0"
                                        ></span>
                                        Free and secure
                                    </div>
                                </div>
                            </div>
                        </label>

                        <!-- Add Card Manually Option -->
                        <label
                            class="flex p-3 items-center self-stretch rounded-lg cursor-pointer transition-all duration-200 relative"
                            :class="
                                howToAddCard ===
                                'add_card_manually'
                                    ? 'border-2 border-[#FD421D]'
                                    : 'border border-slate-300 dark:border-slate-600'
                            "
                        >
                            <input
                                type="radio"
                                class="sr-only"
                                value="add_card_manually"
                                :checked="howToAddCard === 'add_card_manually'"
                                @change="$emit('update:howToAddCard', 'add_card_manually')"
                            />

                            <!-- Radio Circle -->
                            <div class="absolute top-3 right-3">
                                <div
                                    :class="[
                                        'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                                        howToAddCard ===
                                        'add_card_manually'
                                            ? 'border-[#FD421D] bg-[#FD421D]'
                                            : 'border-slate-300 bg-white dark:bg-slate-800',
                                    ]"
                                >
                                    <div
                                        v-if="
                                            howToAddCard ===
                                            'add_card_manually'
                                        "
                                        class="w-2 h-2 bg-white dark:bg-slate-800 rounded-full"
                                    ></div>
                                </div>
                            </div>

                            <!-- Content Container -->
                            <div
                                class="flex flex-col items-start gap-2 flex-1"
                            >
                                <!-- Icon Container -->
                                <div
                                    class="flex w-5 h-5 justify-start items-center"
                                >
                                    <Icon
                                        icon="edit"
                                        class="w-5 h-5 text-slate-600 dark:text-slate-300"
                                    />
                                </div>

                                <!-- Title -->
                                <h3
                                    class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                >
                                    Add card manually
                                </h3>

                                <!-- Features List -->
                                <div
                                    class="flex flex-col items-start gap-1 self-stretch"
                                >
                                    <div
                                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 flex items-center"
                                    >
                                        <span
                                            class="w-1 h-1 bg-slate-400 rounded-full mr-2 flex-shrink-0"
                                        ></span>
                                        Add individual cards by
                                        entering details
                                        manually
                                    </div>
                                    <div
                                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 flex items-center"
                                    >
                                        <span
                                            class="w-1 h-1 bg-slate-400 rounded-full mr-2 flex-shrink-0"
                                        ></span>
                                        No automatic updates,
                                        edit when needed
                                    </div>
                                    <div
                                        class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 flex items-center"
                                    >
                                        <span
                                            class="w-1 h-1 bg-slate-400 rounded-full mr-2 flex-shrink-0"
                                        ></span>
                                        Suitable if your bank
                                        isn't supported
                                    </div>
                                </div>
                            </div>
                        </label>
                    </div>
                </div>

                <div class="w-full" v-show="errors.length > 0">
                    <Errors :errors="errors" />
                </div>
            </div>

            <!-- Footer Area - Always at bottom -->
            <div
                class="flex flex-row items-center p-6 bg-white dark:bg-slate-800 flex-shrink-0 mt-auto w-full"
                :class="isStep1Mobile ? 'hidden' : ''"
            >
                <div
                    class="flex items-center gap-2 rounded cursor-pointer select-none"
                    @click="$emit('update:showSecurityInfo', true)"
                >
                    <Icon
                        icon="security"
                        class="w-5 h-5 flex-shrink-0 text-slate-600 dark:text-slate-300"
                    />
                    <span
                        class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                    >
                        How we keep your data safe
                    </span>
                </div>
                <div class="ml-auto flex items-center gap-2">
                    <button
                        type="button"
                        @click.prevent="$emit('close')"
                        class="flex py-2.5 px-4 items-start gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        @click.prevent="$emit('gotoStep', 3)"
                        class="flex py-2.5 px-4 justify-center items-center gap-2 rounded bg-slate-800 dark:bg-slate-600 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-500 transition-colors"
                        id="hs-wallet-connect-next"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
        <!-- Mobile bottom bar: full-width Next -->
        <div
            v-if="isStep1Mobile"
            class="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]"
        >
            <button
                type="button"
                @click.prevent="$emit('gotoStep', 3)"
                class="w-full py-3 rounded-md bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium leading-5 hover:bg-slate-700 dark:hover:bg-slate-500 active:bg-slate-700"
                id="hs-wallet-connect-next-mobile"
            >
                Next
            </button>
        </div>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import Errors from '@/components/errors.vue';

defineProps({
    connectStep: { type: Number, required: true },
    users: { type: Array, required: true },
    accountUserForCard: { type: [String, Number], default: '' },
    howToAddCard: { type: String, default: '' },
    errors: { type: Array, default: () => [] },
    isStep1Mobile: { type: Boolean, default: false },
    showSecurityInfo: { type: Boolean, default: false },
    selectedUserName: { type: String, default: '' },
    showUserDropdown: { type: Boolean, default: false },
    userDropdownStyles: { type: Object, default: () => ({}) },
});

defineEmits([
    'close',
    'gotoStep',
    'update:accountUserForCard',
    'update:howToAddCard',
    'update:showSecurityInfo',
    'toggleUserDropdown',
    'selectUser',
]);
</script>
